/**
 * Admin Service Client
 *
 * Provides a centralized client for all admin-related operations.
 * Replaces direct Supabase RPC calls with API calls to the admin microservice.
 *
 * Features:
 * - Type-safe operations for admin functions
 * - Automatic error handling
 * - Support for admin stats, user management, and role management
 */

import { API_ENDPOINTS } from "@/config/api";
import { getAuthToken, resolveApiRequestHeaders } from "./assets/assetHelpers";
import {
  ADMIN_API_TIMEOUT_MS,
  createApiCallError,
  parseHttpErrorMessage,
  rethrowAbortError,
} from "./adminServiceHelpers";

/**
 * Make authenticated API call
 */
async function apiCall<T>(endpoint: string, options: globalThis.RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const headers = await resolveApiRequestHeaders(options, token);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ADMIN_API_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const fallbackMessage = `HTTP ${response.status} ${response.statusText}`;
      const message = await parseHttpErrorMessage(response, fallbackMessage);
      throw createApiCallError(message, response.status, endpoint);
    }

    return response.json();
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    rethrowAbortError(error, ADMIN_API_TIMEOUT_MS);
  }
}

export interface AdminStats {
  total_users: number;
  active_subscriptions: number;
  total_assets: number;
  total_revenue: number;
  new_users_last_week: number;
  new_users_this_month: number;
  new_users_this_quarter: number;
  new_users_half_year: number;
  new_users_this_year: number;
  most_active_users?: {
    user_id: string;
    email: string;
    display_name: string;
    location: string;
    activity_count: number;
  }[];
}

export const adminService = {
  /**
   * Fetch admin dashboard stats (v3)
   */
  async getAdminStatsV3(): Promise<AdminStats> {
    try {
      const data = await apiCall<AdminStats>(API_ENDPOINTS.ADMIN.STATS_V3, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getAdminStatsV3 failed:", error);
      throw error;
    }
  },

  /**
   * Fetch admin dashboard stats (legacy)
   */
  async getAdminStats(): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(API_ENDPOINTS.ADMIN.STATS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getAdminStats failed:", error);
      throw error;
    }
  },

  /**
   * Fetch subscription stats with emails
   */
  async getSubscriptionStats(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(API_ENDPOINTS.ADMIN.SUBSCRIPTION_STATS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getSubscriptionStats failed:", error);
      throw error;
    }
  },

  /**
   * Fetch asset stats with user info
   */
  async getAssetStats(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(API_ENDPOINTS.ADMIN.ASSET_STATS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getAssetStats failed:", error);
      throw error;
    }
  },

  /**
   * Fetch all admin assets
   */
  async getAllAdminAssets(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(API_ENDPOINTS.ADMIN.ASSETS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getAllAdminAssets failed:", error);
      throw error;
    }
  },

  /**
   * Recover a deleted user
   */
  async recoverDeletedUser(userId: string): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/recover`, {
        method: "POST",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] recoverDeletedUser failed:", error);
      throw error;
    }
  },

  /**
   * Update user subscription type
   */
  async updateSubscriptionType(
    userId: string,
    subscriptionType: string,
    amount: number
  ): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/subscription`,
        {
          method: "PUT",
          body: JSON.stringify({
            subscription_type: subscriptionType,
            amount,
          }),
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] updateSubscriptionType failed:", error);
      throw error;
    }
  },

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: string): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      });
      return data;
    } catch (error) {
      console.warn("[AdminService] updateUserRole failed:", error);
      throw error;
    }
  },

  /**
   * Check if current user is super admin
   */
  async checkSuperAdmin(): Promise<boolean> {
    try {
      const data = await apiCall<{ isSuperAdmin: boolean }>(API_ENDPOINTS.ADMIN.CHECK_SUPER_ADMIN, {
        method: "GET",
      });
      return data.isSuperAdmin;
    } catch (error) {
      console.error("[AdminService] checkSuperAdmin failed:", error);
      return false;
    }
  },

  /**
   * Check if current user is admin
   */
  async checkAdmin(): Promise<boolean> {
    try {
      const data = await apiCall<{ isAdmin: boolean }>(API_ENDPOINTS.ADMIN.CHECK_ADMIN, {
        method: "GET",
      });
      return data.isAdmin;
    } catch (error: unknown) {
      console.error("[AdminService] checkAdmin failed:", error);
      return false;
    }
  },

  async listSecurityEvents(limit = 100): Promise<Record<string, unknown>[]> {
    const data = await apiCall<{ events: Record<string, unknown>[] }>(
      `${API_ENDPOINTS.ADMIN.SECURITY_EVENTS}?limit=${limit}`,
      { method: "GET" }
    );
    return data.events ?? [];
  },

  async listDeletedUsers(): Promise<Record<string, unknown>[]> {
    const data = await apiCall<{ users: Record<string, unknown>[] }>(
      API_ENDPOINTS.ADMIN.DELETED_USERS,
      { method: "GET" }
    );
    return data.users ?? [];
  },

  /**
   * Fetch user sessions for activity tracking
   */
  async getUserSessions(userId: string, fetchLogouts: boolean = false): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/sessions?fetchLogouts=${fetchLogouts}`,
        {
          method: "GET",
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] getUserSessions failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user subscriptions for activity tracking
   */
  async getUserSubscriptions(userId: string): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/subscriptions`,
        {
          method: "GET",
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] getUserSubscriptions failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user transactions for activity tracking
   */
  async getUserTransactions(userId: string): Promise<unknown[]> {
    try {
      return await apiCall<unknown[]>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/transactions`, {
        method: "GET",
      });
    } catch (error) {
      console.error("[AdminService] getUserTransactions failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user assets for activity tracking
   */
  async getUserAssets(userId: string): Promise<unknown[]> {
    try {
      return await apiCall<unknown[]>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/assets`, {
        method: "GET",
      });
    } catch (error) {
      console.error("[AdminService] getUserAssets failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user auth logs (sessions + privacy consents)
   */
  async getUserAuthLogs(userId: string): Promise<{ sessions: unknown[]; consents: unknown[] }> {
    try {
      const data = await apiCall<{ sessions: unknown[]; consents: unknown[] }>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/auth-logs`,
        {
          method: "GET",
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] getUserAuthLogs failed:", error);
      throw error;
    }
  },

  /**
   * Find user by exact email
   * Used for admin operations like setting user as admin
   */
  async findUserByEmail(
    email: string
  ): Promise<{ id: string; email: string; name?: string; role?: string } | null> {
    try {
      const data = await apiCall<{ id: string; email: string; name?: string; role?: string }>(
        `${API_ENDPOINTS.ADMIN.FIND_USER_BY_EMAIL}?email=${encodeURIComponent(email)}`,
        {
          method: "GET",
        }
      );
      return data;
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string };
      // If user not found (404), return null
      if (err.status === 404 || err.message?.includes("not found")) {
        return null;
      }
      console.error("[AdminService] findUserByEmail failed:", error);
      throw error;
    }
  },

  /**
   * Fetch all users (admin only)
   */
  async getAllUsers(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(`${API_ENDPOINTS.ADMIN.BASE}/users/all`, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[AdminService] getAllUsers failed:", error);
      throw error;
    }
  },

  /**
   * Search users by email pattern
   */
  async searchUsersByEmailPattern(emailPattern: string): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/search?emailPattern=${encodeURIComponent(emailPattern)}`,
        {
          method: "GET",
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] searchUsersByEmailPattern failed:", error);
      throw error;
    }
  },

  /**
   * Create a user (admin only)
   */
  async createUser(payload: {
    email: string;
    password: string;
    fullName: string;
    role: "user" | "admin";
  }): Promise<{
    success: boolean;
    warning?: string;
    message?: string;
    error?: string;
    user?: { id: string; email?: string };
  }> {
    try {
      const data = await apiCall<{
        success: boolean;
        warning?: string;
        message?: string;
        error?: string;
        user?: { id: string; email?: string };
      }>(API_ENDPOINTS.ADMIN.CREATE_USER, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return data;
    } catch (error) {
      console.error("[AdminService] createUser failed:", error);
      throw error;
    }
  },

  /**
   * Soft delete a user (archived, recoverable)
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await apiCall<void>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("[AdminService] deleteUser failed:", error);
      throw error;
    }
  },

  /**
   * Disable (ban) a user account without deleting data
   */
  async banUser(userId: string, reason?: string): Promise<void> {
    try {
      await apiCall<void>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/ban`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
    } catch (error) {
      console.error("[AdminService] banUser failed:", error);
      throw error;
    }
  },

  /**
   * Re-enable a previously disabled (banned) account
   */
  async unbanUser(userId: string): Promise<void> {
    try {
      await apiCall<void>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/unban`, {
        method: "POST",
      });
    } catch (error) {
      console.error("[AdminService] unbanUser failed:", error);
      throw error;
    }
  },

  /**
   * Hard delete a user (permanent deletion)
   */
  async hardDeleteUser(userId: string): Promise<void> {
    try {
      await apiCall<void>(`${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/hard`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("[AdminService] hardDeleteUser failed:", error);
      throw error;
    }
  },

  /**
   * Batch delete users (soft or hard)
   */
  async batchDeleteUsers(userIds: string[], deleteType: "soft" | "hard"): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(`${API_ENDPOINTS.ADMIN.BASE}/users/batch-delete`, {
        method: "POST",
        body: JSON.stringify({ userIds, deleteType }),
      });
      return data;
    } catch (error) {
      console.error("[AdminService] batchDeleteUsers failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user activity logs
   */
  async fetchUserActivity(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(API_ENDPOINTS.ADMIN.USER_ACTIVITY, {
        method: "GET",
      });
      return (data || []).map((item) => {
        const record = item as Record<string, unknown>;
        return {
          ...record,
          ip_address: (record.ip_address as string | null) ?? null,
        };
      });
    } catch (error) {
      console.error("[AdminService] fetchUserActivity failed:", error);
      throw error;
    }
  },

  /**
   * Resend verification email to a user
   */
  async resendVerificationEmail(userId: string, email: string): Promise<unknown> {
    try {
      const data = await apiCall<unknown>(
        `${API_ENDPOINTS.ADMIN.BASE}/users/${userId}/resend-verification`,
        {
          method: "POST",
          body: JSON.stringify({ email }),
        }
      );
      return data;
    } catch (error) {
      console.error("[AdminService] resendVerificationEmail failed:", error);
      throw error;
    }
  },

  /**
   * Fetch user sessions with emails
   */
  async fetchUserSessions(): Promise<unknown[]> {
    try {
      const data = await apiCall<unknown[]>(API_ENDPOINTS.ADMIN.USER_SESSIONS, {
        method: "GET",
      });
      return data || [];
    } catch (error) {
      console.error("[AdminService] fetchUserSessions failed:", error);
      throw error;
    }
  },
};
